import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconAlertTriangle, IconArrowLeft } from "@tabler/icons-react"
import { useState } from "react"
import InvoiceReviewRow, { type Entry, type RowValues } from "./components/InvoiceReviewRow"

interface Option {
  id: number
  name: string
}

interface StatusOption {
  value: string
  label: string
}

interface Props {
  [key: string]: unknown
  entries: Entry[]
  customers: Option[]
  currencies: string[]
  statuses: StatusOption[]
}

function initialValues(entry: Entry): RowValues {
  return {
    // Anything already recorded or unreadable starts unticked so a careless
    // "create" cannot duplicate an invoice.
    selected: entry.ready,
    customer_id: entry.customer_id ? String(entry.customer_id) : "",
    cfe_serie: entry.serie || "",
    cfe_number: entry.number || "",
    currency: entry.currency || "usd",
    invoice_date: entry.invoice_date || "",
    due_date: entry.due_date || "",
    total_amount: entry.total_amount != null ? String(entry.total_amount) : "",
    status: "sent",
    notes: "",
    sent_to_accounting: false,
  }
}

export default function InvoiceReview() {
  const { entries, customers, currencies, statuses } = usePage<Props>().props

  // A single upload opens straight into the side-by-side view; a batch stays
  // collapsed so the whole list is visible at once.
  const [expanded, setExpanded] = useState<string[]>(entries.length === 1 ? [entries[0].signed_blob_id] : [])

  const { data, setData, post, processing, errors, transform } = useForm<{ rows: RowValues[] }>({
    rows: entries.map(initialValues),
  })

  const allErrors = Object.values(errors)
  const selectedCount = data.rows.filter((row) => row.selected).length
  const attentionCount = entries.filter((entry) => !entry.ready).length

  function updateRow(index: number, field: keyof RowValues, value: string | boolean) {
    setData(
      "rows",
      data.rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    )
  }

  function toggleExpanded(id: string) {
    setExpanded((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const invoices = data.rows
      .map((row, index) => ({ row, entry: entries[index] }))
      .filter(({ row }) => row.selected)
      .map(({ row, entry }) => ({
        signed_blob_id: entry.signed_blob_id,
        customer_id: row.customer_id,
        cfe_serie: row.cfe_serie,
        cfe_number: row.cfe_number,
        currency: row.currency,
        invoice_date: row.invoice_date,
        due_date: row.due_date,
        total_amount: row.total_amount,
        status: row.status,
        notes: row.notes,
        sent_to_accounting: row.sent_to_accounting,
      }))

    // The form holds one row per uploaded file; only the ticked ones are sent.
    transform(() => ({ invoices }) as unknown as { rows: RowValues[] })
    post("/invoices/confirm_upload")
  }

  const submitLabel = processing
    ? "Saving..."
    : `Create ${selectedCount} ${selectedCount === 1 ? "invoice" : "invoices"}`

  return (
    <>
      <Head title="Review Invoices - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Review</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/invoices/upload">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">
                Review {entries.length} {entries.length === 1 ? "invoice" : "invoices"}
              </h1>
              <p className="text-muted-foreground text-sm">
                Expand a row to check it against the PDF. Nothing is saved until you confirm.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            {allErrors.length > 0 && (
              <CardContent className="pb-0">
                <div className="border-destructive/20 bg-destructive/5 rounded-lg border p-3">
                  <ul className="text-destructive space-y-1 text-sm">
                    {allErrors.map((err) => (
                      <li key={String(err)}>{String(err)}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            )}

            {attentionCount > 0 && (
              <CardContent className="pb-0">
                <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
                  <IconAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    {attentionCount} of {entries.length} need attention and are not ticked. Expand them to see why.
                  </p>
                </div>
              </CardContent>
            )}

            <CardContent className="px-0">
              <div className="divide-y">
                {entries.map((entry, index) => (
                  <InvoiceReviewRow
                    key={entry.signed_blob_id}
                    entry={entry}
                    values={data.rows[index]}
                    expanded={expanded.includes(entry.signed_blob_id)}
                    customers={customers}
                    currencies={currencies}
                    statuses={statuses}
                    onToggleExpanded={() => toggleExpanded(entry.signed_blob_id)}
                    onChange={(field, value) => updateRow(index, field, value)}
                  />
                ))}
              </div>
            </CardContent>

            <CardFooter className="justify-between border-t">
              <p className="text-muted-foreground text-sm">
                {selectedCount} of {entries.length} selected
                {attentionCount > 0 ? ` - ${attentionCount} need attention` : ""}
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" asChild>
                  <a href="/invoices/upload">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || selectedCount === 0}>
                  {submitLabel}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </form>
      </div>
    </>
  )
}

InvoiceReview.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
